import React, { useState, useEffect } from 'react';
import { 
  Award, 
  CheckCircle2, 
  Clock, 
  Edit3, 
  Save, 
  X, 
  Calendar, 
  Plus, 
  Trash2, 
  Layers, 
  HelpCircle, 
  BookOpen, 
  Sliders
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import { EDUCATION_DEFAULTS } from '../../../data/educationDefaults';
import './FormsUnified.css';

const getAuthHeaders = () => {
  const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

export default function EducationAssessmentSystemForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const defaults = EDUCATION_DEFAULTS.assessment_system || {};
  const defaultsMeaning = [
    { title: 'Personalized Learning', description: 'Each student receives targeted support based on their unique strengths and needs.', icon: 'book' },
    { title: 'Clear Progress Tracking', description: 'Regular data helps us identify growth and celebrate achievements along the way.', icon: 'chart' },
    { title: 'Open Communication', description: 'Parents stay informed with detailed reports and regular conferences throughout the year.', icon: 'users' },
    { title: 'Growth Mindset Focus', description: 'Feedback emphasizes effort and improvement, building lasting confidence and resilience.', icon: 'zap' }
  ];
  const defaultsCalendar = [
    { period: 'Aug - Sep', title: 'Diagnostic Baselines', description: "Initial assessments to understand each student's starting point and learning profile.", icon: 'calendar', activities: ['Initial skill mapping and learning profile', 'Personalized learning pathway design', 'Baseline data for progress tracking'] },
    { period: 'Sep - Nov', title: 'Formative Checks & Projects', description: 'Weekly observations, exit tickets, and first performance tasks.', icon: 'clipboard', activities: ['Weekly checks and mastery rubrics', 'Student-led reflection and goal-setting', 'Teacher-student conferencing'] },
    { period: 'Nov - Dec', title: 'Mid-Term Assessment', description: 'Consolidation of learning and feedback ahead of the new semester.', icon: 'chart', activities: ['Culminating projects and portfolios', 'Standards-referenced assessments', 'Progress reports to families'] }
  ];

  const [formData, setFormData] = useState({
    kicker: defaults.kicker || 'Assessment System',
    title: defaults.title || "How students' progress is evaluated",
    summary: defaults.summary || 'We use a balanced assessment system that values daily growth, authentic performance, and mastery of standards.',
    balanced_approach_title: defaults.balanced_approach_title || 'Our Balanced Approach',
    balanced_approach_description: defaults.balanced_approach_description || "Assessment at Permata Harapan goes beyond traditional testing. We combine multiple methods to capture the full picture of each student's learning journey—from daily growth to mastery of standards.",
    assessment_methodology_title: defaults.assessment_methodology_title || 'Our Assessment Methodology',
    assessment_methodology_subtitle: defaults.assessment_methodology_subtitle || 'Four pillars that guide how we evaluate and support student learning',
    assessment_calendar_header_title: defaults.assessment_calendar_header_title || 'Assessment Calendar',
    assessment_calendar_header_subtitle: defaults.assessment_calendar_header_subtitle || 'Key assessment touchpoints throughout the academic year',
    points: defaults.points ? defaults.points.map(p => ({ ...p, details: Array.isArray(p.details) ? [...p.details] : [] })) : [],
    methodology: defaults.methodology ? defaults.methodology.map(m => ({ ...m })) : [],
    what_this_means: defaultsMeaning,
    assessment_calendar: defaultsCalendar
  });

  const [originalData, setOriginalData] = useState({ ...formData });
  const [pendingFields, setPendingFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [activeTab, setActiveTab] = useState('overview');

  // Modals state
  const [editingPointIndex, setEditingPointIndex] = useState(null);
  const [pointModalData, setPointModalData] = useState(null);
  const [editingMethodIndex, setEditingMethodIndex] = useState(null);
  const [methodModalData, setMethodModalData] = useState(null);
  const [editingMeaningIndex, setEditingMeaningIndex] = useState(null);
  const [meaningModalData, setMeaningModalData] = useState(null);
  const [editingCalendarIndex, setEditingCalendarIndex] = useState(null);
  const [calendarModalData, setCalendarModalData] = useState(null);
  const [newDetailItem, setNewDetailItem] = useState('');
  const [newActivityItem, setNewActivityItem] = useState('');

  useEffect(() => {
    loadSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadSection = async () => {
    setLoading(true);
    try {
      let section = null;

      const adminResponse = await fetch(
        `${apiBase}/education/cms/sections`,
        { credentials: 'include', headers: getAuthHeaders() }
      );

      if (adminResponse.ok) {
        const result = await adminResponse.json();
        if (result.ok && Array.isArray(result.data)) {
          section = result.data.find(s => s.section_type === 'education-assessment-system-shell') ||
                    result.data.find(s => s.section_type === 'education-assessment-system');
        }
      }

      if (!section) {
        const publicResponse = await fetch(
          `${apiBase}/education/cms/sections?published=true`,
          { credentials: 'include', headers: getAuthHeaders() }
        );

        if (publicResponse.ok) {
          const result = await publicResponse.json();
          if (result.ok && Array.isArray(result.data)) {
            section = result.data.find(s => s.section_type === 'education-assessment-system-shell') ||
                      result.data.find(s => s.section_type === 'education-assessment-system');
          }
        }
      }

      if (section) {
        setSectionId(section.id);

        let content = section.content_published || section.content || {};
        if (typeof content === 'string') {
          try { content = JSON.parse(content); } catch (e) { content = {}; }
        }
        const en = content.en || {};

        const initialData = {
          kicker: en.kicker || defaults.kicker || 'Assessment System',
          title: en.title || defaults.title || "How students' progress is evaluated",
          summary: en.summary || defaults.summary || 'We use a balanced assessment system that values daily growth, authentic performance, and mastery of standards.',
          balanced_approach_title: en.balanced_approach_title || defaults.balanced_approach_title || 'Our Balanced Approach',
          balanced_approach_description: en.balanced_approach_description || defaults.balanced_approach_description || "Assessment at Permata Harapan goes beyond traditional testing. We combine multiple methods to capture the full picture of each student's learning journey—from daily growth to mastery of standards.",
          assessment_methodology_title: en.assessment_methodology_title || defaults.assessment_methodology_title || 'Our Assessment Methodology',
          assessment_methodology_subtitle: en.assessment_methodology_subtitle || defaults.assessment_methodology_subtitle || 'Four pillars that guide how we evaluate and support student learning',
          assessment_calendar_header_title: en.assessment_calendar_header_title || defaults.assessment_calendar_header_title || 'Assessment Calendar',
          assessment_calendar_header_subtitle: en.assessment_calendar_header_subtitle || defaults.assessment_calendar_header_subtitle || 'Key assessment touchpoints throughout the academic year',
          points: Array.isArray(en.points) && en.points.length > 0 
            ? en.points.map(p => ({ ...p, details: Array.isArray(p.details) ? [...p.details] : [] })) 
            : defaults.points ? defaults.points.map(p => ({ ...p, details: Array.isArray(p.details) ? [...p.details] : [] })) : [],
          methodology: Array.isArray(en.methodology) && en.methodology.length > 0 
            ? en.methodology.map(m => ({ ...m })) 
            : defaults.methodology ? defaults.methodology.map(m => ({ ...m })) : [],
          what_this_means: Array.isArray(en.what_this_means) && en.what_this_means.length > 0 
            ? en.what_this_means.map(m => ({ ...m })) 
            : defaultsMeaning,
          assessment_calendar: Array.isArray(en.assessment_calendar) && en.assessment_calendar.length > 0 
            ? en.assessment_calendar.map(c => ({ ...c, activities: Array.isArray(c.activities) ? [...c.activities] : [] })) 
            : defaultsCalendar
        };

        const pendingFieldsMap = {};
        try {
          const pendingResponse = await fetch(
            `${apiBase}/cms/section/${section.id}/with-pending`,
            { credentials: 'include', headers: getAuthHeaders() }
          );

          if (pendingResponse.ok) {
            const pendingResult = await pendingResponse.json();
            if (pendingResult.success && pendingResult.data && pendingResult.data.pending_changes) {
              const { pending_changes } = pendingResult.data;
              if (Array.isArray(pending_changes)) {
                pending_changes.forEach(change => {
                  if (change.status === 'pending' && (change.locale === 'en' || !change.locale)) {
                    let val = change.new_value;
                    if (typeof val === 'string') {
                      try { val = JSON.parse(val); } catch (e) {}
                    }

                    if (change.field_id === 'kicker') {
                      pendingFieldsMap.kicker = true;
                      if (typeof val === 'string') initialData.kicker = val;
                    } else if (change.field_id === 'title') {
                      pendingFieldsMap.title = true;
                      if (typeof val === 'string') initialData.title = val;
                    } else if (change.field_id === 'summary') {
                      pendingFieldsMap.summary = true;
                      if (typeof val === 'string') initialData.summary = val;
                    } else if (change.field_id === 'balanced_approach_title') {
                      pendingFieldsMap.balanced_approach_title = true;
                      if (typeof val === 'string') initialData.balanced_approach_title = val;
                    } else if (change.field_id === 'balanced_approach_description') {
                      pendingFieldsMap.balanced_approach_description = true;
                      if (typeof val === 'string') initialData.balanced_approach_description = val;
                    } else if (change.field_id === 'assessment_methodology_title') {
                      pendingFieldsMap.assessment_methodology_title = true;
                      if (typeof val === 'string') initialData.assessment_methodology_title = val;
                    } else if (change.field_id === 'assessment_methodology_subtitle') {
                      pendingFieldsMap.assessment_methodology_subtitle = true;
                      if (typeof val === 'string') initialData.assessment_methodology_subtitle = val;
                    } else if (change.field_id === 'assessment_calendar_header_title') {
                      pendingFieldsMap.assessment_calendar_header_title = true;
                      if (typeof val === 'string') initialData.assessment_calendar_header_title = val;
                    } else if (change.field_id === 'assessment_calendar_header_subtitle') {
                      pendingFieldsMap.assessment_calendar_header_subtitle = true;
                      if (typeof val === 'string') initialData.assessment_calendar_header_subtitle = val;
                    } else if (change.field_id && change.field_id.includes('points.')) {
                      const match = change.field_id.match(/points\.(\d+)\./);
                      if (match) {
                        const idx = parseInt(match[1], 10);
                        pendingFieldsMap[`point_${idx}`] = true;
                        if (val && initialData.points[idx]) {
                          initialData.points[idx] = {
                            ...val,
                            details: Array.isArray(val.details) ? [...val.details] : []
                          };
                        }
                      }
                    } else if (change.field_id && change.field_id.includes('methodology.')) {
                      const match = change.field_id.match(/methodology\.(\d+)\./);
                      if (match) {
                        const idx = parseInt(match[1], 10);
                        pendingFieldsMap[`method_${idx}`] = true;
                        if (val && initialData.methodology[idx]) {
                          initialData.methodology[idx] = val;
                        }
                      }
                    } else if (change.field_id && change.field_id.includes('what_this_means.')) {
                      const match = change.field_id.match(/what_this_means\.(\d+)\./);
                      if (match) {
                        const idx = parseInt(match[1], 10);
                        pendingFieldsMap[`meaning_${idx}`] = true;
                        if (val && initialData.what_this_means[idx]) {
                          initialData.what_this_means[idx] = val;
                        }
                      }
                    } else if (change.field_id && change.field_id.includes('assessment_calendar.')) {
                      const match = change.field_id.match(/assessment_calendar\.(\d+)\./);
                      if (match) {
                        const idx = parseInt(match[1], 10);
                        pendingFieldsMap[`calendar_${idx}`] = true;
                        if (val && initialData.assessment_calendar[idx]) {
                          initialData.assessment_calendar[idx] = {
                            ...val,
                            activities: Array.isArray(val.activities) ? [...val.activities] : []
                          };
                        }
                      }
                    }
                  }
                });
              }
            }
          }
        } catch (err) {
          console.warn('[EducationAssessmentSystemForm] Pending changes check failed:', err.message);
        }

        setFormData(initialData);
        setOriginalData(JSON.parse(JSON.stringify(initialData)));
        setPendingFields(pendingFieldsMap);
      } else {
        toast.error('Assessment System section not found');
      }
    } catch (err) {
      console.error('[EducationAssessmentSystemForm] Load error:', err);
      toast.error('Failed to load assessment section: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  // Submit Single Field
  const handleSubmitSingleField = async (fieldName, label) => {
    if (!sectionId) {
      toast.error('Assessment section ID not loaded');
      return;
    }

    if (formData[fieldName] === originalData[fieldName]) {
      toast.warning('No changes to submit');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: fieldName,
          field_name: label,
          old_value: originalData[fieldName] || '',
          new_value: formData[fieldName],
          locale: 'en'
        })
      });

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`Proposed change for "${label}" submitted for Principal approval`);
        setPendingFields(prev => ({ ...prev, [fieldName]: true }));
        setOriginalData(prev => ({ ...prev, [fieldName]: formData[fieldName] }));
      } else {
        if (response.status === 409) {
          toast.warning(`A pending proposal for "${label}" is already awaiting review`);
        } else {
          toast.error(result.error || result.message || 'Submission failed');
        }
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast.error(`Error submitting ${label}: ` + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  // Core Assessment Points: Add, Edit, Delete
  const handleOpenAddPointModal = () => {
    const nextIdx = formData.points.length;
    setEditingPointIndex(nextIdx);
    setPointModalData({
      title: '',
      description: '',
      iconType: 'chart',
      details: []
    });
    setNewDetailItem('');
  };

  const openEditPointModal = (index) => {
    setEditingPointIndex(index);
    const p = formData.points[index];
    setPointModalData({
      ...p,
      iconType: p.iconType || p.icon_type || 'chart',
      details: Array.isArray(p.details) ? [...p.details] : []
    });
    setNewDetailItem('');
  };

  const closePointModal = () => {
    setEditingPointIndex(null);
    setPointModalData(null);
    setNewDetailItem('');
  };

  const handleAddPointDetail = () => {
    if (!newDetailItem.trim()) return;
    setPointModalData(prev => ({
      ...prev,
      details: [...(prev.details || []), newDetailItem.trim()]
    }));
    setNewDetailItem('');
  };

  const handleRemovePointDetail = (idx) => {
    setPointModalData(prev => ({
      ...prev,
      details: prev.details.filter((_, i) => i !== idx)
    }));
  };

  const handleSavePoint = async () => {
    if (!pointModalData?.title?.trim()) {
      toast.warning('Please enter point title');
      return;
    }

    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    const isNew = editingPointIndex >= originalData.points.length;
    const op = isNew ? 'add' : 'update';
    const key = `point_${editingPointIndex}`;
    setSubmitting(prev => ({ ...prev, [key]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: `points.${editingPointIndex}.${op}`,
            field_name: `${isNew ? 'Add' : 'Update'} Assessment Point: ${pointModalData.title}`,
            old_value: isNew ? null : (originalData.points[editingPointIndex] || {}),
            new_value: pointModalData,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`Assessment Point "${pointModalData.title}" proposed for Principal approval`);
        setPendingFields(prev => ({ ...prev, [`point_${editingPointIndex}`]: true }));
        const updated = [...formData.points];
        updated[editingPointIndex] = { ...pointModalData };
        setFormData(prev => ({ ...prev, points: updated }));
        closePointModal();
      } else {
        if (response.status === 409) {
          toast.warning(`Point already has a pending proposal`);
        } else {
          toast.error(result.error || result.message || 'Submission failed');
        }
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('Failed to submit point: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleDeletePoint = async (index) => {
    const pointToDelete = formData.points[index];
    if (!pointToDelete) return;

    if (!window.confirm(`Are you sure you want to delete assessment point: "${pointToDelete.title || index + 1}"?`)) {
      return;
    }

    setSubmitting(prev => ({ ...prev, [`del_point_${index}`]: true }));

    try {
      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `points.${index}.delete`,
          field_name: `Delete Assessment Point: ${pointToDelete.title || index + 1}`,
          old_value: pointToDelete,
          new_value: null,
          locale: 'en'
        })
      });

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`Point "${pointToDelete.title}" deletion proposed for Principal approval`);
        setPendingFields(prev => ({ ...prev, [`point_${index}`]: true }));
      } else {
        if (response.status === 409) {
          toast.warning(`Point already has a pending proposal`);
        } else {
          toast.error(result.error || result.message || 'Delete proposal failed');
        }
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to submit point deletion: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [`del_point_${index}`]: false }));
    }
  };

  // Methodology Pillars: Add, Edit, Delete
  const handleOpenAddMethodModal = () => {
    const nextIdx = formData.methodology.length;
    setEditingMethodIndex(nextIdx);
    setMethodModalData({
      phase: '',
      description: ''
    });
  };

  const openEditMethodModal = (index) => {
    setEditingMethodIndex(index);
    setMethodModalData({ ...formData.methodology[index] });
  };

  const closeMethodModal = () => {
    setEditingMethodIndex(null);
    setMethodModalData(null);
  };

  const handleSaveMethod = async () => {
    if (!methodModalData?.phase?.trim()) {
      toast.warning('Please enter phase / pillar name');
      return;
    }

    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    const isNew = editingMethodIndex >= originalData.methodology.length;
    const op = isNew ? 'add' : 'update';
    const key = `method_${editingMethodIndex}`;
    setSubmitting(prev => ({ ...prev, [key]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: `methodology.${editingMethodIndex}.${op}`,
            field_name: `${isNew ? 'Add' : 'Update'} Methodology: ${methodModalData.phase}`,
            old_value: isNew ? null : (originalData.methodology[editingMethodIndex] || {}),
            new_value: methodModalData,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`Methodology "${methodModalData.phase}" proposed for Principal approval`);
        setPendingFields(prev => ({ ...prev, [`method_${editingMethodIndex}`]: true }));
        const updated = [...formData.methodology];
        updated[editingMethodIndex] = { ...methodModalData };
        setFormData(prev => ({ ...prev, methodology: updated }));
        closeMethodModal();
      } else {
        if (response.status === 409) {
          toast.warning(`Methodology already has a pending proposal`);
        } else {
          toast.error(result.error || result.message || 'Submission failed');
        }
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('Failed to submit methodology: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleDeleteMethod = async (index) => {
    const methodToDelete = formData.methodology[index];
    if (!methodToDelete) return;

    if (!window.confirm(`Are you sure you want to delete methodology pillar: "${methodToDelete.phase || index + 1}"?`)) {
      return;
    }

    setSubmitting(prev => ({ ...prev, [`del_method_${index}`]: true }));

    try {
      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `methodology.${index}.delete`,
          field_name: `Delete Methodology Pillar: ${methodToDelete.phase || index + 1}`,
          old_value: methodToDelete,
          new_value: null,
          locale: 'en'
        })
      });

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`Methodology "${methodToDelete.phase}" deletion proposed for Principal approval`);
        setPendingFields(prev => ({ ...prev, [`method_${index}`]: true }));
      } else {
        if (response.status === 409) {
          toast.warning(`Methodology already has a pending proposal`);
        } else {
          toast.error(result.error || result.message || 'Delete proposal failed');
        }
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to submit methodology deletion: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [`del_method_${index}`]: false }));
    }
  };

  // What This Means: Edit Modal
  const openEditMeaningModal = (index) => {
    setEditingMeaningIndex(index);
    setMeaningModalData({ ...formData.what_this_means[index] });
  };

  const closeMeaningModal = () => {
    setEditingMeaningIndex(null);
    setMeaningModalData(null);
  };

  const handleSaveMeaning = async () => {
    if (!meaningModalData?.title?.trim()) {
      toast.warning('Please enter title');
      return;
    }

    const key = `meaning_${editingMeaningIndex}`;
    setSubmitting(prev => ({ ...prev, [key]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: `what_this_means.${editingMeaningIndex}.update`,
            field_name: `Update Meaning ${editingMeaningIndex + 1}: ${meaningModalData.title}`,
            old_value: originalData.what_this_means[editingMeaningIndex] || {},
            new_value: meaningModalData,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`Item "${meaningModalData.title}" proposed for Principal approval`);
        setPendingFields(prev => ({ ...prev, [`meaning_${editingMeaningIndex}`]: true }));
        const updated = [...formData.what_this_means];
        updated[editingMeaningIndex] = { ...meaningModalData };
        setFormData(prev => ({ ...prev, what_this_means: updated }));
        closeMeaningModal();
      } else {
        if (response.status === 409) {
          toast.warning(`Item ${editingMeaningIndex + 1} already has a pending proposal`);
        } else {
          toast.error(result.error || result.message || 'Submission failed');
        }
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('Failed to submit item: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  // Assessment Calendar: Edit Modal
  const openEditCalendarModal = (index) => {
    setEditingCalendarIndex(index);
    const item = formData.assessment_calendar[index];
    setCalendarModalData({
      ...item,
      activities: Array.isArray(item.activities) ? [...item.activities] : []
    });
    setNewActivityItem('');
  };

  const closeCalendarModal = () => {
    setEditingCalendarIndex(null);
    setCalendarModalData(null);
    setNewActivityItem('');
  };

  const handleAddCalendarActivity = () => {
    if (!newActivityItem.trim()) return;
    setCalendarModalData(prev => ({
      ...prev,
      activities: [...(prev.activities || []), newActivityItem.trim()]
    }));
    setNewActivityItem('');
  };

  const handleRemoveCalendarActivity = (idx) => {
    setCalendarModalData(prev => ({
      ...prev,
      activities: prev.activities.filter((_, i) => i !== idx)
    }));
  };

  const handleSaveCalendar = async () => {
    if (!calendarModalData?.period?.trim()) {
      toast.warning('Please enter calendar period');
      return;
    }

    const key = `calendar_${editingCalendarIndex}`;
    setSubmitting(prev => ({ ...prev, [key]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: `assessment_calendar.${editingCalendarIndex}.update`,
            field_name: `Update Assessment Calendar ${editingCalendarIndex + 1}: ${calendarModalData.period}`,
            old_value: originalData.assessment_calendar[editingCalendarIndex] || {},
            new_value: calendarModalData,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`Calendar Period "${calendarModalData.period}" proposed for Principal approval`);
        setPendingFields(prev => ({ ...prev, [`calendar_${editingCalendarIndex}`]: true }));
        const updated = [...formData.assessment_calendar];
        updated[editingCalendarIndex] = { ...calendarModalData };
        setFormData(prev => ({ ...prev, assessment_calendar: updated }));
        closeCalendarModal();
      } else {
        if (response.status === 409) {
          toast.warning(`Calendar item ${editingCalendarIndex + 1} already has a pending proposal`);
        } else {
          toast.error(result.error || result.message || 'Submission failed');
        }
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('Failed to submit calendar item: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-form-container">
        <div className="unified-loading-container">
          <div className="unified-loading-spinner"></div>
          <p className="unified-loading-text">Loading Assessment System content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-left">
          <div className="unified-icon-badge">
            <Award size={22} />
          </div>
          <div>
            <h2 className="unified-title">Assessment System & Evaluation</h2>
            <p className="unified-subtitle">
              Manage evaluation philosophy, assessment dimensions, methodology cycle, student values, and milestone timeline
            </p>
          </div>
        </div>
      </div>

      {/* Sub-tabs Navigation */}
      <div className="unified-tab-bar" style={{ marginBottom: '24px' }}>
        <button
          type="button"
          className={`tab-pill-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <BookOpen size={16} />
          Overview
        </button>
        <button
          type="button"
          className={`tab-pill-btn ${activeTab === 'balanced-approach' ? 'active' : ''}`}
          onClick={() => setActiveTab('balanced-approach')}
        >
          <Sliders size={16} />
          Balanced Approach & Points ({formData.points?.length || 0})
        </button>
        <button
          type="button"
          className={`tab-pill-btn ${activeTab === 'methodology' ? 'active' : ''}`}
          onClick={() => setActiveTab('methodology')}
        >
          <Layers size={16} />
          Methodology ({formData.methodology?.length || 0})
        </button>
        <button
          type="button"
          className={`tab-pill-btn ${activeTab === 'meaning' ? 'active' : ''}`}
          onClick={() => setActiveTab('meaning')}
        >
          <HelpCircle size={16} />
          What This Means ({formData.what_this_means?.length || 0})
        </button>
        <button
          type="button"
          className={`tab-pill-btn ${activeTab === 'calendar' ? 'active' : ''}`}
          onClick={() => setActiveTab('calendar')}
        >
          <Calendar size={16} />
          Assessment Calendar ({formData.assessment_calendar?.length || 0})
        </button>
      </div>

      {/* TAB 1: OVERVIEW */}
      {activeTab === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div>
            <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
              Assessment System Header & Overview
            </h3>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '2px 0 0' }}>
              Primary kicker badge, section title headline, and assessment summary
            </p>
          </div>

          {/* Section Kicker */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Section Tag / Kicker</label>
              {pendingFields.kicker && (
                <span className="unified-pending-pill">
                  <Clock size={12} /> Pending Review
                </span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                className="unified-input"
                value={formData.kicker}
                onChange={(e) => handleFieldChange('kicker', e.target.value)}
                disabled={pendingFields.kicker || submitting.kicker}
                placeholder="e.g. Assessment System"
                maxLength={60}
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitSingleField('kicker', 'Section Kicker')}
                disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
              >
                <Save size={14} /> {submitting.kicker ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>

          {/* Title */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Section Title / Headline</label>
              {pendingFields.title && (
                <span className="unified-pending-pill">
                  <Clock size={12} /> Pending Review
                </span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                className="unified-input"
                value={formData.title}
                onChange={(e) => handleFieldChange('title', e.target.value)}
                disabled={pendingFields.title || submitting.title}
                placeholder="e.g. How students' progress is evaluated"
                maxLength={120}
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitSingleField('title', 'Section Title')}
                disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
              >
                <Save size={14} /> {submitting.title ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>

          {/* Summary */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Assessment Summary</label>
              {pendingFields.summary && (
                <span className="unified-pending-pill">
                  <Clock size={12} /> Pending Review
                </span>
              )}
            </div>
            <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
              <textarea
                className="unified-textarea"
                value={formData.summary}
                onChange={(e) => handleFieldChange('summary', e.target.value)}
                disabled={pendingFields.summary || submitting.summary}
                rows={4}
                placeholder="We use a balanced assessment system that values daily growth, authentic performance, and mastery of standards."
                maxLength={500}
              />
              <button
                type="button"
                className="unified-submit-button"
                style={{ marginTop: '2px' }}
                onClick={() => handleSubmitSingleField('summary', 'Assessment Summary')}
                disabled={submitting.summary || pendingFields.summary || formData.summary === originalData.summary}
              >
                <Save size={14} /> {submitting.summary ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: BALANCED APPROACH & POINTS */}
      {activeTab === 'balanced-approach' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Header Card */}
          <div className="unified-field-card" style={{ borderLeftColor: '#2563eb' }}>
            <div className="unified-field-header">
              <label className="unified-label" style={{ color: '#1d4ed8' }}>
                Balanced Approach Overview
              </label>
              {(pendingFields.balanced_approach_title || pendingFields.balanced_approach_description) && (
                <span className="unified-pending-pill">
                  <Clock size={12} /> Pending Review
                </span>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                  Approach Title
                </span>
                <div className="unified-input-action-row">
                  <input
                    type="text"
                    className="unified-input"
                    value={formData.balanced_approach_title}
                    onChange={(e) => handleFieldChange('balanced_approach_title', e.target.value)}
                    disabled={pendingFields.balanced_approach_title || submitting.balanced_approach_title}
                    placeholder="e.g. Our Balanced Approach"
                  />
                  <button
                    type="button"
                    className="unified-submit-button"
                    onClick={() => handleSubmitSingleField('balanced_approach_title', 'Balanced Approach Title')}
                    disabled={submitting.balanced_approach_title || pendingFields.balanced_approach_title || formData.balanced_approach_title === originalData.balanced_approach_title}
                  >
                    <Save size={14} /> {submitting.balanced_approach_title ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>

              <div>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                  Approach Description
                </span>
                <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
                  <textarea
                    className="unified-textarea"
                    value={formData.balanced_approach_description}
                    onChange={(e) => handleFieldChange('balanced_approach_description', e.target.value)}
                    disabled={pendingFields.balanced_approach_description || submitting.balanced_approach_description}
                    rows={3}
                    placeholder="Explain how diagnostic, formative, and summative assessments coordinate..."
                  />
                  <button
                    type="button"
                    className="unified-submit-button"
                    style={{ marginTop: '2px' }}
                    onClick={() => handleSubmitSingleField('balanced_approach_description', 'Balanced Approach Description')}
                    disabled={submitting.balanced_approach_description || pendingFields.balanced_approach_description || formData.balanced_approach_description === originalData.balanced_approach_description}
                  >
                    <Save size={14} /> {submitting.balanced_approach_description ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Points List */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
              <div>
                <h3 style={{ fontSize: '15px', fontWeight: 800, color: '#0f172a', margin: 0 }}>
                  Core Assessment Points ({formData.points?.length || 0})
                </h3>
                <p style={{ fontSize: '12.5px', color: '#64748b', margin: '2px 0 0' }}>
                  Key assessment dimensions with bullet details and automatic icon badges
                </p>
              </div>
              <button
                type="button"
                className="unified-submit-button"
                onClick={handleOpenAddPointModal}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
              >
                <Plus size={16} /> Add Assessment Point
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {Array.isArray(formData.points) && formData.points.map((point, idx) => {
                const isPending = pendingFields[`point_${idx}`];
                return (
                  <div
                    key={idx}
                    className="unified-field-card"
                    style={{
                      borderLeftColor: '#3b82f6',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}
                  >
                    <div className="unified-field-header">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '28px',
                          height: '28px',
                          borderRadius: '8px',
                          background: '#eff6ff',
                          color: '#2563eb',
                          fontWeight: 800,
                          fontSize: '13px'
                        }}>
                          {idx + 1}
                        </span>
                        <div>
                          <h4 style={{ fontSize: '15px', fontWeight: 800, color: '#0f172a', margin: 0 }}>
                            {point.title}
                          </h4>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {isPending && (
                          <span className="unified-pending-pill">
                            <Clock size={12} /> Pending Review
                          </span>
                        )}
                        <button
                          type="button"
                          className="unified-secondary-btn"
                          onClick={() => openEditPointModal(idx)}
                          disabled={isPending}
                        >
                          <Edit3 size={14} />
                          Edit Point
                        </button>
                        <button
                          type="button"
                          className="unified-secondary-btn"
                          onClick={() => handleDeletePoint(idx)}
                          disabled={isPending || submitting[`del_point_${idx}`]}
                          style={{ color: '#ef4444', borderColor: '#fca5a5' }}
                        >
                          <Trash2 size={14} />
                          {submitting[`del_point_${idx}`] ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </div>

                    <p style={{ fontSize: '13.5px', color: '#475569', margin: 0, lineHeight: 1.5 }}>
                      {point.description}
                    </p>

                    {Array.isArray(point.details) && point.details.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                        {point.details.map((detail, dIdx) => (
                          <span
                            key={dIdx}
                            style={{
                              background: '#f8fafc',
                              color: '#334155',
                              padding: '3px 8px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              border: '1px solid #e2e8f0',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <CheckCircle2 size={11} color="#2563eb" />
                            {detail}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: METHODOLOGY */}
      {activeTab === 'methodology' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Header Card */}
          <div className="unified-field-card" style={{ borderLeftColor: '#8b5cf6' }}>
            <div className="unified-field-header">
              <label className="unified-label" style={{ color: '#6d28d9' }}>
                Methodology Framework Header
              </label>
              {(pendingFields.assessment_methodology_title || pendingFields.assessment_methodology_subtitle) && (
                <span className="unified-pending-pill">
                  <Clock size={12} /> Pending Review
                </span>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                  Methodology Title
                </span>
                <div className="unified-input-action-row">
                  <input
                    type="text"
                    className="unified-input"
                    value={formData.assessment_methodology_title}
                    onChange={(e) => handleFieldChange('assessment_methodology_title', e.target.value)}
                    disabled={pendingFields.assessment_methodology_title || submitting.assessment_methodology_title}
                    placeholder="e.g. Our Assessment Methodology"
                  />
                  <button
                    type="button"
                    className="unified-submit-button"
                    onClick={() => handleSubmitSingleField('assessment_methodology_title', 'Methodology Title')}
                    disabled={submitting.assessment_methodology_title || pendingFields.assessment_methodology_title || formData.assessment_methodology_title === originalData.assessment_methodology_title}
                  >
                    <Save size={14} /> {submitting.assessment_methodology_title ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>

              <div>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                  Methodology Subtitle
                </span>
                <div className="unified-input-action-row">
                  <input
                    type="text"
                    className="unified-input"
                    value={formData.assessment_methodology_subtitle}
                    onChange={(e) => handleFieldChange('assessment_methodology_subtitle', e.target.value)}
                    disabled={pendingFields.assessment_methodology_subtitle || submitting.assessment_methodology_subtitle}
                    placeholder="e.g. Four pillars that guide how we evaluate and support student learning"
                  />
                  <button
                    type="button"
                    className="unified-submit-button"
                    onClick={() => handleSubmitSingleField('assessment_methodology_subtitle', 'Methodology Subtitle')}
                    disabled={submitting.assessment_methodology_subtitle || pendingFields.assessment_methodology_subtitle || formData.assessment_methodology_subtitle === originalData.assessment_methodology_subtitle}
                  >
                    <Save size={14} /> {submitting.assessment_methodology_subtitle ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Pillars List */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
              <div>
                <h3 style={{ fontSize: '15px', fontWeight: 800, color: '#0f172a', margin: 0 }}>
                  Methodology Pillars ({formData.methodology?.length || 0})
                </h3>
                <p style={{ fontSize: '12.5px', color: '#64748b', margin: '2px 0 0' }}>
                  Continuous cycle stages (Diagnostic, Formative Checks, Authentic Projects, Feedback)
                </p>
              </div>
              <button
                type="button"
                className="unified-submit-button"
                onClick={handleOpenAddMethodModal}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
              >
                <Plus size={16} /> Add Methodology Pillar
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {Array.isArray(formData.methodology) && formData.methodology.map((phase, idx) => {
                const isPending = pendingFields[`method_${idx}`];
                return (
                  <div
                    key={idx}
                    className="unified-field-card"
                    style={{
                      borderLeftColor: '#8b5cf6',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px'
                    }}
                  >
                    <div className="unified-field-header">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '28px',
                          height: '28px',
                          borderRadius: '8px',
                          background: '#f5f3ff',
                          color: '#8b5cf6',
                          fontWeight: 800,
                          fontSize: '13px'
                        }}>
                          {idx + 1}
                        </span>
                        <h4 style={{ fontSize: '15px', fontWeight: 800, color: '#0f172a', margin: 0 }}>
                          {phase.phase}
                        </h4>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {isPending && (
                          <span className="unified-pending-pill">
                            <Clock size={12} /> Pending Review
                          </span>
                        )}
                        <button
                          type="button"
                          className="unified-secondary-btn"
                          onClick={() => openEditMethodModal(idx)}
                          disabled={isPending}
                        >
                          <Edit3 size={14} />
                          Edit Pillar
                        </button>
                        <button
                          type="button"
                          className="unified-secondary-btn"
                          onClick={() => handleDeleteMethod(idx)}
                          disabled={isPending || submitting[`del_method_${idx}`]}
                          style={{ color: '#ef4444', borderColor: '#fca5a5' }}
                        >
                          <Trash2 size={14} />
                          {submitting[`del_method_${idx}`] ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </div>

                    <p style={{ fontSize: '13.5px', color: '#475569', margin: 0, lineHeight: 1.5 }}>
                      {phase.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: WHAT THIS MEANS FOR YOUR CHILD */}
      {activeTab === 'meaning' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 800, color: '#0f172a', margin: '0 0 4px 0' }}>
                What This Means for Your Child ({formData.what_this_means?.length || 0})
              </h3>
              <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                Tangible values and benefits of our evaluation approach for families and students
              </p>
            </div>
          </div>

          {/* Automatic Icon Info Box */}
          <div style={{
            padding: '12px 16px',
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: '10px',
            fontSize: '13px',
            color: '#166534',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <CheckCircle2 size={18} color="#16a34a" style={{ flexShrink: 0 }} />
            <span>
              <strong>Icon Otomatis:</strong> Icon pada bagian ini ditetapkan secara otomatis oleh sistem berdasarkan preset pedagogis (Book, Chart, Users, Zap) sehingga tampil serasi di halaman utama.
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
            {Array.isArray(formData.what_this_means) && formData.what_this_means.map((item, idx) => {
              const isPending = pendingFields[`meaning_${idx}`];
              return (
                <div
                  key={idx}
                  className="unified-field-card"
                  style={{
                    borderLeftColor: '#059669',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}
                >
                  <div>
                    <div className="unified-field-header" style={{ marginBottom: '8px' }}>
                      <h4 style={{ fontSize: '15px', fontWeight: 800, color: '#0f172a', margin: 0 }}>
                        {item.title}
                      </h4>
                      {isPending && (
                        <span className="unified-pending-pill">
                          <Clock size={12} /> Pending Review
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: '13px', color: '#475569', lineHeight: 1.5, margin: 0 }}>
                      {item.description}
                    </p>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px', paddingTop: '10px', borderTop: '1px solid #f1f5f9' }}>
                    <button
                      type="button"
                      className="unified-secondary-btn"
                      onClick={() => openEditMeaningModal(idx)}
                      disabled={isPending}
                    >
                      <Edit3 size={14} />
                      Edit Benefit
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* TAB 5: ASSESSMENT CALENDAR */}
      {activeTab === 'calendar' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Header Card */}
          <div className="unified-field-card" style={{ borderLeftColor: '#d97706' }}>
            <div className="unified-field-header">
              <label className="unified-label" style={{ color: '#b45309' }}>
                Assessment Calendar Header
              </label>
              {(pendingFields.assessment_calendar_header_title || pendingFields.assessment_calendar_header_subtitle) && (
                <span className="unified-pending-pill">
                  <Clock size={12} /> Pending Review
                </span>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                  Calendar Title
                </span>
                <div className="unified-input-action-row">
                  <input
                    type="text"
                    className="unified-input"
                    value={formData.assessment_calendar_header_title}
                    onChange={(e) => handleFieldChange('assessment_calendar_header_title', e.target.value)}
                    disabled={pendingFields.assessment_calendar_header_title || submitting.assessment_calendar_header_title}
                    placeholder="e.g. Assessment Calendar"
                  />
                  <button
                    type="button"
                    className="unified-submit-button"
                    onClick={() => handleSubmitSingleField('assessment_calendar_header_title', 'Calendar Title')}
                    disabled={submitting.assessment_calendar_header_title || pendingFields.assessment_calendar_header_title || formData.assessment_calendar_header_title === originalData.assessment_calendar_header_title}
                  >
                    <Save size={14} /> {submitting.assessment_calendar_header_title ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>

              <div>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                  Calendar Subtitle
                </span>
                <div className="unified-input-action-row">
                  <input
                    type="text"
                    className="unified-input"
                    value={formData.assessment_calendar_header_subtitle}
                    onChange={(e) => handleFieldChange('assessment_calendar_header_subtitle', e.target.value)}
                    disabled={pendingFields.assessment_calendar_header_subtitle || submitting.assessment_calendar_header_subtitle}
                    placeholder="e.g. Key assessment touchpoints throughout the academic year"
                  />
                  <button
                    type="button"
                    className="unified-submit-button"
                    onClick={() => handleSubmitSingleField('assessment_calendar_header_subtitle', 'Calendar Subtitle')}
                    disabled={submitting.assessment_calendar_header_subtitle || pendingFields.assessment_calendar_header_subtitle || formData.assessment_calendar_header_subtitle === originalData.assessment_calendar_header_subtitle}
                  >
                    <Save size={14} /> {submitting.assessment_calendar_header_subtitle ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Calendar Milestones */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
              <div>
                <h3 style={{ fontSize: '15px', fontWeight: 800, color: '#0f172a', margin: 0 }}>
                  Assessment Milestones ({formData.assessment_calendar?.length || 0})
                </h3>
                <p style={{ fontSize: '12.5px', color: '#64748b', margin: '2px 0 0' }}>
                  Scheduled checkpoint periods and touchpoint activities
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {Array.isArray(formData.assessment_calendar) && formData.assessment_calendar.map((item, idx) => {
                const isPending = pendingFields[`calendar_${idx}`];
                return (
                  <div
                    key={idx}
                    className="unified-field-card"
                    style={{
                      borderLeftColor: '#d97706',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}
                  >
                    <div className="unified-field-header">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          background: '#fef3c7',
                          color: '#b45309',
                          fontWeight: 800,
                          fontSize: '12.5px'
                        }}>
                          {item.period || `Stage ${idx + 1}`}
                        </span>
                        <h4 style={{ fontSize: '15px', fontWeight: 800, color: '#0f172a', margin: 0 }}>
                          {item.title || item.period}
                        </h4>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {isPending && (
                          <span className="unified-pending-pill">
                            <Clock size={12} /> Pending Review
                          </span>
                        )}
                        <button
                          type="button"
                          className="unified-secondary-btn"
                          onClick={() => openEditCalendarModal(idx)}
                          disabled={isPending}
                        >
                          <Edit3 size={14} />
                          Edit Milestone
                        </button>
                      </div>
                    </div>

                    {item.description && (
                      <p style={{ fontSize: '13.5px', color: '#475569', margin: 0, lineHeight: 1.5 }}>
                        {item.description}
                      </p>
                    )}

                    {Array.isArray(item.activities) && item.activities.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                        {item.activities.map((act, aIdx) => (
                          <span
                            key={aIdx}
                            style={{
                              background: '#f8fafc',
                              color: '#334155',
                              padding: '3px 8px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              border: '1px solid #e2e8f0',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <CheckCircle2 size={11} color="#d97706" />
                            {act}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* POINT EDIT / ADD MODAL */}
      {editingPointIndex !== null && pointModalData && (
        <div className="unified-modal-overlay" onClick={closePointModal}>
          <div className="unified-modal" onClick={(e) => e.stopPropagation()}>
            <div className="unified-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Sliders size={20} color="#2563eb" />
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                  {editingPointIndex >= originalData.points.length ? 'Add Assessment Point' : `Edit Point: ${pointModalData.title}`}
                </h3>
              </div>
              <button
                type="button"
                onClick={closePointModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="unified-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              <div>
                <label className="unified-label" style={{ marginBottom: '8px', display: 'block' }}>
                  Point Title *
                </label>
                <input
                  type="text"
                  className="unified-input"
                  value={pointModalData.title || ''}
                  onChange={(e) => setPointModalData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g. Diagnostic Baselines"
                  maxLength={100}
                />
              </div>

              <div>
                <label className="unified-label" style={{ marginBottom: '8px', display: 'block' }}>
                  Description *
                </label>
                <textarea
                  className="unified-textarea"
                  value={pointModalData.description || ''}
                  onChange={(e) => setPointModalData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  placeholder="Early assessments identify strengths and learning needs so teaching targets the right starting point."
                  maxLength={500}
                />
              </div>

              {/* Point Details Bullet Items */}
              <div>
                <label className="unified-label" style={{ marginBottom: '8px', display: 'block' }}>
                  Key Detail Checkpoints
                </label>
                
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                  <input
                    type="text"
                    className="unified-input"
                    value={newDetailItem}
                    onChange={(e) => setNewDetailItem(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddPointDetail();
                      }
                    }}
                    placeholder="e.g. Individual skill mapping and learning profile"
                  />
                  <button
                    type="button"
                    className="unified-secondary-btn"
                    onClick={handleAddPointDetail}
                  >
                    <Plus size={14} />
                    Add
                  </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {pointModalData.details?.map((detail, dIdx) => (
                    <div
                      key={dIdx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        padding: '6px 12px'
                      }}
                    >
                      <span style={{ fontSize: '13px', color: '#1e293b' }}>{detail}</span>
                      <button
                        type="button"
                        onClick={() => handleRemovePointDetail(dIdx)}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="unified-modal-footer">
              <button type="button" className="unified-secondary-btn" onClick={closePointModal}>
                Cancel
              </button>
              <button
                type="button"
                className="unified-submit-button"
                onClick={handleSavePoint}
                disabled={submitting[`point_${editingPointIndex}`]}
              >
                <Save size={16} />
                {submitting[`point_${editingPointIndex}`] ? 'Submitting...' : 'Submit Point Proposal'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* METHODOLOGY EDIT / ADD MODAL */}
      {editingMethodIndex !== null && methodModalData && (
        <div className="unified-modal-overlay" onClick={closeMethodModal}>
          <div className="unified-modal" onClick={(e) => e.stopPropagation()}>
            <div className="unified-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Layers size={20} color="#8b5cf6" />
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                  {editingMethodIndex >= originalData.methodology.length ? 'Add Methodology Pillar' : `Edit Pillar: ${methodModalData.phase}`}
                </h3>
              </div>
              <button
                type="button"
                onClick={closeMethodModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="unified-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              <div>
                <label className="unified-label" style={{ marginBottom: '8px', display: 'block' }}>
                  Phase / Pillar Name *
                </label>
                <input
                  type="text"
                  className="unified-input"
                  value={methodModalData.phase || ''}
                  onChange={(e) => setMethodModalData(prev => ({ ...prev, phase: e.target.value }))}
                  placeholder="e.g. Diagnostic Baselines, Formative Daily Checks, Summative Mastery"
                  maxLength={80}
                />
              </div>

              <div>
                <label className="unified-label" style={{ marginBottom: '8px', display: 'block' }}>
                  Description *
                </label>
                <textarea
                  className="unified-textarea"
                  value={methodModalData.description || ''}
                  onChange={(e) => setMethodModalData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  placeholder="Describe the assessment methodology and teacher-student actions..."
                  maxLength={500}
                />
              </div>
            </div>

            <div className="unified-modal-footer">
              <button type="button" className="unified-secondary-btn" onClick={closeMethodModal}>
                Cancel
              </button>
              <button
                type="button"
                className="unified-submit-button"
                onClick={handleSaveMethod}
                disabled={submitting[`method_${editingMethodIndex}`]}
              >
                <Save size={16} />
                {submitting[`method_${editingMethodIndex}`] ? 'Submitting...' : 'Submit Pillar Proposal'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MEANING EDIT MODAL */}
      {editingMeaningIndex !== null && meaningModalData && (
        <div className="unified-modal-overlay" onClick={closeMeaningModal}>
          <div className="unified-modal" onClick={(e) => e.stopPropagation()}>
            <div className="unified-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <HelpCircle size={20} color="#059669" />
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                  Edit Value: {meaningModalData.title}
                </h3>
              </div>
              <button
                type="button"
                onClick={closeMeaningModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="unified-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              <div>
                <label className="unified-label" style={{ marginBottom: '8px', display: 'block' }}>
                  Value Title *
                </label>
                <input
                  type="text"
                  className="unified-input"
                  value={meaningModalData.title || ''}
                  onChange={(e) => setMeaningModalData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g. Personalized Learning"
                  maxLength={80}
                />
              </div>

              <div>
                <label className="unified-label" style={{ marginBottom: '8px', display: 'block' }}>
                  Description *
                </label>
                <textarea
                  className="unified-textarea"
                  value={meaningModalData.description || ''}
                  onChange={(e) => setMeaningModalData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  placeholder="How this assessment principle translates into positive student growth..."
                  maxLength={500}
                />
              </div>
            </div>

            <div className="unified-modal-footer">
              <button type="button" className="unified-secondary-btn" onClick={closeMeaningModal}>
                Cancel
              </button>
              <button
                type="button"
                className="unified-submit-button"
                onClick={handleSaveMeaning}
                disabled={submitting[`meaning_${editingMeaningIndex}`]}
              >
                <Save size={16} />
                {submitting[`meaning_${editingMeaningIndex}`] ? 'Submitting...' : 'Submit Value Proposal'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CALENDAR EDIT MODAL */}
      {editingCalendarIndex !== null && calendarModalData && (
        <div className="unified-modal-overlay" onClick={closeCalendarModal}>
          <div className="unified-modal" onClick={(e) => e.stopPropagation()}>
            <div className="unified-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Calendar size={20} color="#d97706" />
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                  Edit Milestone: {calendarModalData.period}
                </h3>
              </div>
              <button
                type="button"
                onClick={closeCalendarModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="unified-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              <div>
                <label className="unified-label" style={{ marginBottom: '8px', display: 'block' }}>
                  Time Period / Date Label *
                </label>
                <input
                  type="text"
                  className="unified-input"
                  value={calendarModalData.period || ''}
                  onChange={(e) => setCalendarModalData(prev => ({ ...prev, period: e.target.value }))}
                  placeholder="e.g. Aug - Sep, Nov - Dec"
                  maxLength={60}
                />
              </div>

              <div>
                <label className="unified-label" style={{ marginBottom: '8px', display: 'block' }}>
                  Milestone Title *
                </label>
                <input
                  type="text"
                  className="unified-input"
                  value={calendarModalData.title || ''}
                  onChange={(e) => setCalendarModalData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g. Diagnostic Baselines"
                  maxLength={100}
                />
              </div>

              <div>
                <label className="unified-label" style={{ marginBottom: '8px', display: 'block' }}>
                  Description
                </label>
                <textarea
                  className="unified-textarea"
                  value={calendarModalData.description || ''}
                  onChange={(e) => setCalendarModalData(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                  placeholder="Brief description of the milestone..."
                  maxLength={400}
                />
              </div>

              {/* Activities List */}
              <div>
                <label className="unified-label" style={{ marginBottom: '8px', display: 'block' }}>
                  Milestone Activities
                </label>
                
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                  <input
                    type="text"
                    className="unified-input"
                    value={newActivityItem}
                    onChange={(e) => setNewActivityItem(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCalendarActivity();
                      }
                    }}
                    placeholder="e.g. Weekly checks and mastery rubrics"
                  />
                  <button
                    type="button"
                    className="unified-secondary-btn"
                    onClick={handleAddCalendarActivity}
                  >
                    <Plus size={14} />
                    Add
                  </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {calendarModalData.activities?.map((activity, aIdx) => (
                    <div
                      key={aIdx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        padding: '6px 12px'
                      }}
                    >
                      <span style={{ fontSize: '13px', color: '#1e293b' }}>{activity}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveCalendarActivity(aIdx)}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="unified-modal-footer">
              <button type="button" className="unified-secondary-btn" onClick={closeCalendarModal}>
                Cancel
              </button>
              <button
                type="button"
                className="unified-submit-button"
                onClick={handleSaveCalendar}
                disabled={submitting[`calendar_${editingCalendarIndex}`]}
              >
                <Save size={16} />
                {submitting[`calendar_${editingCalendarIndex}`] ? 'Submitting...' : 'Submit Milestone Proposal'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
